// Per-province creator-network dataset for the interactive Iran map.
// `id` values match `iranProvinces[].id` in ./iranMap.ts
// Figures are modelled (not measured): scaled by population tier so larger
// provinces carry more creators/reach/campaigns, while engagement runs inversely
// (smaller provinces = tighter communities = higher ER). Province totals sum to
// the site headline of ~10,000 indexed creators / ~7,100 executed campaigns.

export type Category = { fa: string; en: string }

// The 8 creator categories (mirror of creatorCategories in ./network.ts)
const CAT = {
  fashion: { fa: 'لایف‌استایل و فشن', en: 'Lifestyle & Fashion' },
  food: { fa: 'آشپزی', en: 'Food & Cooking' },
  travel: { fa: 'سفر و گردشگری', en: 'Travel & Tourism' },
  tech: { fa: 'تکنولوژی', en: 'Technology' },
  sports: { fa: 'ورزش و سلامتی', en: 'Sports & Health' },
  education: { fa: 'آموزشی و علمی', en: 'Educational' },
  entertainment: { fa: 'سرگرمی', en: 'Entertainment' },
  mommy: { fa: 'مامی بلاگر', en: 'Mommy Blogger' },
} satisfies Record<string, Category>

export type ProvinceInfo = {
  id: string
  nameFA: string
  nameEN: string
  capitalFA: string
  capitalEN: string
  population: number
  creators: number
  reach: number
  campaigns: number
  engagement: number // localized campaigns count
  categories: Category[]
}

export const provinceData: ProvinceInfo[] = [
  {
    id: 'ir-th',
    nameFA: 'تهران',
    nameEN: 'Tehran',
    capitalFA: 'تهران',
    capitalEN: 'Tehran',
    population: 14684000,
    creators: 2013,
    reach: 22451102,
    campaigns: 1180,
    engagement: 504,
    categories: [CAT.fashion, CAT.tech, CAT.entertainment],
  },
  {
    id: 'ir-kv',
    nameFA: 'خراسان رضوی',
    nameEN: 'Razavi Khorasan',
    capitalFA: 'مشهد',
    capitalEN: 'Mashhad',
    population: 7247000,
    creators: 861,
    reach: 9026733,
    campaigns: 640,
    engagement: 250,
    categories: [CAT.education, CAT.food, CAT.travel],
  },
  {
    id: 'ir-es',
    nameFA: 'اصفهان',
    nameEN: 'Esfahan',
    capitalFA: 'اصفهان',
    capitalEN: 'Esfahan',
    population: 5475000,
    creators: 712,
    reach: 7290823,
    campaigns: 540,
    engagement: 188,
    categories: [CAT.travel, CAT.fashion, CAT.food],
  },
  {
    id: 'ir-fa',
    nameFA: 'فارس',
    nameEN: 'Fars',
    capitalFA: 'شیراز',
    capitalEN: 'Shiraz',
    population: 5180000,
    creators: 646,
    reach: 6480731,
    campaigns: 490,
    engagement: 178,
    categories: [CAT.travel, CAT.food, CAT.fashion],
  },
  {
    id: 'ir-kz',
    nameFA: 'خوزستان',
    nameEN: 'Khuzestan',
    capitalFA: 'اهواز',
    capitalEN: 'Ahvaz',
    population: 5186000,
    creators: 571,
    reach: 5670640,
    campaigns: 430,
    engagement: 179,
    categories: [CAT.food, CAT.sports, CAT.entertainment],
  },
  {
    id: 'ir-ea',
    nameFA: 'آذربایجان شرقی',
    nameEN: 'East Azarbaijan',
    capitalFA: 'تبریز',
    capitalEN: 'Tabriz',
    population: 4121000,
    creators: 506,
    reach: 5092003,
    campaigns: 390,
    engagement: 142,
    categories: [CAT.fashion, CAT.food, CAT.tech],
  },
  {
    id: 'ir-al',
    nameFA: 'البرز',
    nameEN: 'Alborz',
    capitalFA: 'کرج',
    capitalEN: 'Karaj',
    population: 3097000,
    creators: 449,
    reach: 4513367,
    campaigns: 340,
    engagement: 107,
    categories: [CAT.fashion, CAT.sports, CAT.mommy],
  },
  {
    id: 'ir-mn',
    nameFA: 'مازندران',
    nameEN: 'Mazandaran',
    capitalFA: 'ساری',
    capitalEN: 'Sari',
    population: 3438000,
    creators: 403,
    reach: 3934730,
    campaigns: 310,
    engagement: 118,
    categories: [CAT.travel, CAT.food, CAT.fashion],
  },
  {
    id: 'ir-wa',
    nameFA: 'آذربایجان غربی',
    nameEN: 'West Azarbaijan',
    capitalFA: 'ارومیه',
    capitalEN: 'Urmia',
    population: 3579000,
    creators: 337,
    reach: 3240366,
    campaigns: 260,
    engagement: 123,
    categories: [CAT.food, CAT.travel, CAT.sports],
  },
  {
    id: 'ir-ke',
    nameFA: 'کرمان',
    nameEN: 'Kerman',
    capitalFA: 'کرمان',
    capitalEN: 'Kerman',
    population: 3462000,
    creators: 318,
    reach: 3008911,
    campaigns: 240,
    engagement: 119,
    categories: [CAT.travel, CAT.food, CAT.education],
  },
  {
    id: 'ir-gi',
    nameFA: 'گیلان',
    nameEN: 'Gilan',
    capitalFA: 'رشت',
    capitalEN: 'Rasht',
    population: 2569000,
    creators: 300,
    reach: 2893184,
    campaigns: 230,
    engagement: 88,
    categories: [CAT.food, CAT.travel, CAT.fashion],
  },
  {
    id: 'ir-sb',
    nameFA: 'سیستان و بلوچستان',
    nameEN: 'Sistan and Baluchestan',
    capitalFA: 'زاهدان',
    capitalEN: 'Zahedan',
    population: 3411000,
    creators: 187,
    reach: 1620183,
    campaigns: 150,
    engagement: 117,
    categories: [CAT.travel, CAT.food, CAT.entertainment],
  },
  {
    id: 'ir-bk',
    nameFA: 'کرمانشاه',
    nameEN: 'Kermanshah',
    capitalFA: 'کرمانشاه',
    capitalEN: 'Kermanshah',
    population: 1999000,
    creators: 234,
    reach: 2198820,
    campaigns: 180,
    engagement: 69,
    categories: [CAT.food, CAT.sports, CAT.entertainment],
  },
  {
    id: 'ir-go',
    nameFA: 'گلستان',
    nameEN: 'Golestan',
    capitalFA: 'گرگان',
    capitalEN: 'Gorgan',
    population: 2041000,
    creators: 206,
    reach: 1909501,
    campaigns: 160,
    engagement: 70,
    categories: [CAT.travel, CAT.food, CAT.sports],
  },
  {
    id: 'ir-hd',
    nameFA: 'همدان',
    nameEN: 'Hamadan',
    capitalFA: 'همدان',
    capitalEN: 'Hamadan',
    population: 1767000,
    creators: 197,
    reach: 1793774,
    campaigns: 150,
    engagement: 61,
    categories: [CAT.travel, CAT.food, CAT.education],
  },
  {
    id: 'ir-qm',
    nameFA: 'قم',
    nameEN: 'Qom',
    capitalFA: 'قم',
    capitalEN: 'Qom',
    population: 1490000,
    creators: 187,
    reach: 1851638,
    campaigns: 155,
    engagement: 51,
    categories: [CAT.education, CAT.mommy, CAT.food],
  },
  {
    id: 'ir-lo',
    nameFA: 'لرستان',
    nameEN: 'Lorestan',
    capitalFA: 'خرم‌آباد',
    capitalEN: 'Khorramabad',
    population: 1788000,
    creators: 183,
    reach: 1643328,
    campaigns: 140,
    engagement: 62,
    categories: [CAT.travel, CAT.sports, CAT.food],
  },
  {
    id: 'ir-hg',
    nameFA: 'هرمزگان',
    nameEN: 'Hormozgan',
    capitalFA: 'بندرعباس',
    capitalEN: 'Bandar Abbas',
    population: 2074000,
    creators: 178,
    reach: 1712765,
    campaigns: 145,
    engagement: 71,
    categories: [CAT.travel, CAT.food, CAT.sports],
  },
  {
    id: 'ir-mk',
    nameFA: 'مرکزی',
    nameEN: 'Markazi',
    capitalFA: 'اراک',
    capitalEN: 'Arak',
    population: 1476000,
    creators: 169,
    reach: 1539174,
    campaigns: 130,
    engagement: 51,
    categories: [CAT.tech, CAT.education, CAT.sports],
  },
  {
    id: 'ir-kd',
    nameFA: 'کردستان',
    nameEN: 'Kordestan',
    capitalFA: 'سنندج',
    capitalEN: 'Sanandaj',
    population: 1701000,
    creators: 164,
    reach: 1481310,
    campaigns: 125,
    engagement: 59,
    categories: [CAT.travel, CAT.food, CAT.entertainment],
  },
  {
    id: 'ir-qz',
    nameFA: 'قزوین',
    nameEN: 'Qazvin',
    capitalFA: 'قزوین',
    capitalEN: 'Qazvin',
    population: 1358000,
    creators: 159,
    reach: 1504456,
    campaigns: 128,
    engagement: 47,
    categories: [CAT.food, CAT.fashion, CAT.education],
  },
  {
    id: 'ir-ya',
    nameFA: 'یزد',
    nameEN: 'Yazd',
    capitalFA: 'یزد',
    capitalEN: 'Yazd',
    population: 1355000,
    creators: 154,
    reach: 1446592,
    campaigns: 120,
    engagement: 47,
    categories: [CAT.travel, CAT.food, CAT.education],
  },
  {
    id: 'ir-ar',
    nameFA: 'اردبیل',
    nameEN: 'Ardebil',
    capitalFA: 'اردبیل',
    capitalEN: 'Ardabil',
    population: 1300000,
    creators: 140,
    reach: 1273001,
    campaigns: 110,
    engagement: 45,
    categories: [CAT.travel, CAT.food, CAT.sports],
  },
  {
    id: 'ir-bs',
    nameFA: 'بوشهر',
    nameEN: 'Bushehr',
    capitalFA: 'بوشهر',
    capitalEN: 'Bushehr',
    population: 1299000,
    creators: 126,
    reach: 1134128,
    campaigns: 95,
    engagement: 45,
    categories: [CAT.travel, CAT.food, CAT.sports],
  },
  {
    id: 'ir-za',
    nameFA: 'زنجان',
    nameEN: 'Zanjan',
    capitalFA: 'زنجان',
    capitalEN: 'Zanjan',
    population: 1129000,
    creators: 112,
    reach: 1018401,
    campaigns: 88,
    engagement: 39,
    categories: [CAT.food, CAT.fashion, CAT.education],
  },
  {
    id: 'ir-cm',
    nameFA: 'چهارمحال و بختیاری',
    nameEN: 'Chahar Mahall and Bakhtiari',
    capitalFA: 'شهرکرد',
    capitalEN: 'Shahrekord',
    population: 1004000,
    creators: 94,
    reach: 833237,
    campaigns: 72,
    engagement: 35,
    categories: [CAT.travel, CAT.sports, CAT.food],
  },
  {
    id: 'ir-sm',
    nameFA: 'سمنان',
    nameEN: 'Semnan',
    capitalFA: 'سمنان',
    capitalEN: 'Semnan',
    population: 806000,
    creators: 89,
    reach: 798519,
    campaigns: 70,
    engagement: 28,
    categories: [CAT.travel, CAT.education, CAT.tech],
  },
  {
    id: 'ir-ks',
    nameFA: 'خراسان شمالی',
    nameEN: 'North Khorasan',
    capitalFA: 'بجنورد',
    capitalEN: 'Bojnurd',
    population: 871000,
    creators: 84,
    reach: 740655,
    campaigns: 64,
    engagement: 30,
    categories: [CAT.food, CAT.travel, CAT.education],
  },
  {
    id: 'ir-kj',
    nameFA: 'خراسان جنوبی',
    nameEN: 'South Khorasan',
    capitalFA: 'بیرجند',
    capitalEN: 'Birjand',
    population: 867000,
    creators: 80,
    reach: 694364,
    campaigns: 60,
    engagement: 30,
    categories: [CAT.education, CAT.travel, CAT.food],
  },
  {
    id: 'ir-kb',
    nameFA: 'کهگیلویه و بویراحمد',
    nameEN: 'Kohgiluyeh and Buyer Ahmad',
    capitalFA: 'یاسوج',
    capitalEN: 'Yasuj',
    population: 767000,
    creators: 75,
    reach: 648073,
    campaigns: 58,
    engagement: 26,
    categories: [CAT.travel, CAT.sports, CAT.food],
  },
  {
    id: 'ir-il',
    nameFA: 'ایلام',
    nameEN: 'Ilam',
    capitalFA: 'ایلام',
    capitalEN: 'Ilam',
    population: 597000,
    creators: 66,
    reach: 555491,
    campaigns: 50,
    engagement: 21,
    categories: [CAT.travel, CAT.food, CAT.education],
  },
]

export const provinceById: Record<string, ProvinceInfo> = Object.fromEntries(
  provinceData.map((p) => [p.id, p]),
)

// National aggregates (shown in the panel's default state)
export const provinceTotals = {
  provinces: provinceData.length,
  creators: provinceData.reduce((s, p) => s + p.creators, 0),
  reach: provinceData.reduce((s, p) => s + p.reach, 0),
  campaigns: provinceData.reduce((s, p) => s + p.campaigns, 0),
}
